import React, { useState, useEffect } from 'react';
import './Arena.css';

const BASE = 'https://fotileams.com/api';
const authHeaders = () => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${localStorage.getItem('token')}`,
});

// ── Tier identity: mythic fire ascension. Each tier maps to a Chefu
// character image (the Fotile mascot ascending) plus a glow palette.
const CHAR_BASE = 'https://fotileams.com/characters';
const TIER_LOOK = {
  'Ember':         { c1: '#f97316', c2: '#7c2d12', ring: '#fb923c', aura: 'rgba(249,115,22,0.35)', img: 'chefu-ember.webp' },
  'Flamebearer':   { c1: '#f59e0b', c2: '#b45309', ring: '#fbbf24', aura: 'rgba(245,158,11,0.42)', img: 'chefu-flamebearer.webp' },
  'Blaze Knight':  { c1: '#3b82f6', c2: '#1e3a8a', ring: '#60a5fa', aura: 'rgba(59,130,246,0.45)', img: 'chefu-blaze.webp' },
  'Inferno Lord':  { c1: '#a855f7', c2: '#6b21a8', ring: '#c084fc', aura: 'rgba(168,85,247,0.48)', img: 'chefu-inferno.webp' },
  'Phoenix':       { c1: '#fbbf24', c2: '#dc2626', ring: '#fcd34d', aura: 'rgba(251,191,36,0.52)', img: 'chefu-phoenix.webp' },
  'Eternal Flame': { c1: '#ffffff', c2: '#f59e0b', ring: '#ffffff', aura: 'rgba(255,255,255,0.55)', img: 'chefu-eternal.webp' },
};
const lookFor = (tier) => TIER_LOOK[tier] || TIER_LOOK['Ember'];

function fireSize(streak) {
  if (streak >= 20) return '🔥🔥🔥';
  if (streak >= 10) return '🔥🔥';
  if (streak >= 1)  return '🔥';
  return '·';
}

function Counter({ value, className }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    const end = parseInt(value) || 0;
    if (end === 0) { setN(0); return; }
    let cur = 0;
    const step = Math.max(1, Math.ceil(end / 40));
    const t = setInterval(() => {
      cur = Math.min(cur + step, end);
      setN(cur);
      if (cur >= end) clearInterval(t);
    }, 20);
    return () => clearInterval(t);
  }, [value]);
  return <span className={className}>{n.toLocaleString()}</span>;
}

export default function Arena({ employee }) {
  const [player, setPlayer]   = useState(null);
  const [board, setBoard]     = useState([]);
  const [cities, setCities]   = useState([]);
  const [teamGoal, setTeam]   = useState(null);
  const [prizes, setPrizes]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('overall');

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      setLoading(true);
      const [meRes, lbRes, tgRes] = await Promise.all([
        fetch(`${BASE}/league/me`,          { headers: authHeaders() }).then(r => r.json()),
        fetch(`${BASE}/league/leaderboard`, { headers: authHeaders() }).then(r => r.json()),
        fetch(`${BASE}/league/team-goal`,   { headers: authHeaders() }).then(r => r.json()),
      ]);
      if (meRes.player) setPlayer(meRes.player);
      if (meRes.config?.prizes) setPrizes(meRes.config.prizes);
      setBoard(lbRes.leaderboard || []);
      setCities(lbRes.cities || []);
      setTeam(tgRes || null);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="arena-root">
        <div className="arena-loading">
          <div className="arena-loading-fire">🔥</div>
          <div>Entering the Arena…</div>
        </div>
      </div>
    );
  }

  if (!player) {
    return (
      <div className="arena-root">
        <div className="arena-empty">
          <div className="arena-empty-icon">⚔️</div>
          <div className="arena-empty-title">You're not in the Arena yet</div>
          <div className="arena-empty-sub">
            Clock in on time to ignite your flame and begin your ascension.
          </div>
        </div>
      </div>
    );
  }

  const look = lookFor(player.tier);
  const myRank = player.rank;
  const aboveMe = board.find(p => p.rank === myRank - 1);
  const pointsBehind = aboveMe ? aboveMe.month_points - player.month_points : 0;
  const champPrize = prizes.find(p => p.rank === 1);

  return (
    <div className="arena-root">

      {/* ── HERO ── */}
      <div className="arena-hero" style={{ '--glow': look.c1, '--aura': look.aura, '--ring': look.ring }}>
        <div className="arena-tier-label">{player.tier}</div>

        <div className="arena-character">
          <div className="arena-char-aura" />
          <div className="arena-crest-wrap">
            <img
              className="arena-char-img"
              src={`${CHAR_BASE}/${look.img}`}
              alt={player.tier}
              style={{ filter: `drop-shadow(0 6px 22px ${look.aura})` }}
            />
          </div>
          <div className="arena-char-level">
            <span className="arena-char-level-num">{player.level}</span>
            <span className="arena-char-level-lbl">LVL</span>
          </div>
        </div>

        <div className="arena-name">{player.full_name}</div>

        <div className="arena-fire">
          <span className="arena-fire-emoji">{fireSize(player.streak)}</span>
          <span className="arena-fire-count">{player.streak}</span>
          <span className="arena-fire-lbl">day streak</span>
        </div>

        <div className="arena-points-row">
          <div className="arena-point-box">
            <Counter value={player.month_points} className="arena-point-num" />
            <span className="arena-point-lbl">This Month</span>
          </div>
          <div className="arena-point-divider" />
          <div className="arena-point-box">
            <Counter value={player.total_points} className="arena-point-num" />
            <span className="arena-point-lbl">All Time</span>
          </div>
          <div className="arena-point-divider" />
          <div className="arena-point-box">
            <span className="arena-point-num">#{myRank}</span>
            <span className="arena-point-lbl">Rank</span>
          </div>
        </div>
      </div>

      {/* ── STAKES ── */}
      {player.streak > 0 ? (
        <div className="arena-stakes arena-stakes-danger">
          <div className="arena-stakes-icon">⚠️</div>
          <div className="arena-stakes-text">
            <div className="arena-stakes-title">Guard your flame!</div>
            <div className="arena-stakes-sub">
              Late tomorrow and you fall to <b>Level {player.next_level_at_risk}</b> and
              lose <b>{player.stakes_if_late} points</b>. Stay on time to keep ascending.
            </div>
          </div>
        </div>
      ) : (
        <div className="arena-stakes arena-stakes-start">
          <div className="arena-stakes-icon">🔥</div>
          <div className="arena-stakes-text">
            <div className="arena-stakes-title">Ignite your streak</div>
            <div className="arena-stakes-sub">
              Clock in on time to earn <b>{player.stakes_if_late} points</b> and begin your rise.
            </div>
          </div>
        </div>
      )}

      {/* ── CHASE ── */}
      {aboveMe && (
        <div className="arena-chase">
          <span className="arena-chase-fire">⚔️</span>
          <span className="arena-chase-text">
            <b>{pointsBehind} points</b> behind <b>{aboveMe.full_name}</b> at #{myRank - 1}. Overtake them!
          </span>
        </div>
      )}

      {/* ── TEAM GOAL ── */}
      {teamGoal && (
        <div className="arena-team">
          <div className="arena-team-head">
            <span className="arena-team-title">🏆 Team Goal</span>
            <span className="arena-team-pct">{teamGoal.percent}%</span>
          </div>
          <div className="arena-team-bar">
            <div className="arena-team-fill" style={{ width: `${teamGoal.percent}%` }} />
          </div>
          <div className="arena-team-sub">
            {teamGoal.percent >= 100 ? 'Goal smashed! 🎉 Everyone wins.' : 'Every on-time day fuels the team.'}
          </div>
        </div>
      )}

      {/* ── PRIZE ── */}
      {champPrize && (
        <div className="arena-prize">
          <span className="arena-prize-emoji">👑</span>
          <div className="arena-prize-text">
            <div className="arena-prize-amount">PKR {Number(champPrize.amount_pkr).toLocaleString()}</div>
            <div className="arena-prize-lbl">{champPrize.label} — rule the board by month end</div>
          </div>
        </div>
      )}

      {/* ── LEADERBOARD ── */}
      <div className="arena-lb">
        <div className="arena-lb-tabs">
          <button className={`arena-lb-tab ${tab === 'overall' ? 'on' : ''}`} onClick={() => setTab('overall')}>
            Champions
          </button>
          <button className={`arena-lb-tab ${tab === 'city' ? 'on' : ''}`} onClick={() => setTab('city')}>
            City Wars
          </button>
        </div>

        {tab === 'overall' ? (
          <div className="arena-lb-list">
            {board.slice(0, 20).map((p) => {
              const isMe = p.employee_id === player.employee_id;
              const plook = lookFor(p.tier);
              return (
                <div key={p.employee_id} className={`arena-lb-row ${isMe ? 'me' : ''}`}>
                  <div className={`arena-lb-rank ${p.rank <= 3 ? `top${p.rank}` : ''}`}>
                    {p.rank === 1 ? '🥇' : p.rank === 2 ? '🥈' : p.rank === 3 ? '🥉' : p.rank}
                  </div>
                  <div className="arena-lb-crest">
                    <img className="arena-lb-img" src={`${CHAR_BASE}/${plook.img}`} alt={p.tier} />
                  </div>
                  <div className="arena-lb-info">
                    <div className="arena-lb-name">{p.full_name}{isMe && <span className="arena-lb-you"> YOU</span>}</div>
                    <div className="arena-lb-meta">Lv {p.level} · {p.tier} · {fireSize(p.streak)}{p.streak}</div>
                  </div>
                  <div className="arena-lb-pts">{Number(p.month_points).toLocaleString()}</div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="arena-lb-list">
            {cities.map((c, i) => (
              <div key={c.city} className="arena-lb-row">
                <div className={`arena-lb-rank ${i < 3 ? `top${i+1}` : ''}`}>
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                </div>
                <div className="arena-lb-emblem" style={{ background: 'rgba(99,102,241,0.25)' }}>🏙️</div>
                <div className="arena-lb-info">
                  <div className="arena-lb-name">{c.city}</div>
                  <div className="arena-lb-meta">avg per warrior</div>
                </div>
                <div className="arena-lb-pts">{Number(c.avg_points).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="arena-foot">🔥 Clock in on time. Rise through the flames. Become Eternal.</div>
    </div>
  );
}